import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { PracticeLocationInfo } from "../../../redux/slice/practice/practiceLocationSlice";
import CustomRadioButton from "../../common/CustomRadioButton";
import { AddCircleOutlineOutlined, Cancel, Close } from "@mui/icons-material";
import CustomAlert from "../../common/CustomAlert";
import { useTheme } from "@mui/material/styles";
import { useModalStyles } from "../../common/useModalStyles";
import CustomAutocompleteField from "../../common/CustomAutocompleteField";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import { autoAddress, fetchAddressDetails } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";

interface PracticeLocationProps {
  open: boolean;
  onClose: () => void;
  addPractice: (practiceLocation: PracticeLocationInfo) => void;
  practiceLocations?: PracticeLocationInfo[];
  billingAddressPanel: boolean;
  practiceLocationObject: PracticeLocationInfo;
}

export const initialState: any = {
  locationId: undefined,
  practiceId: undefined,
  streetAddress: "",
  unitNumber: "",
  city: "",
  state: "",
  zipCode: "",
  isBillingAddress: "",
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const PracticeLocationPopup: React.FC<PracticeLocationProps> = ({
  open,
  onClose,
  addPractice,
  practiceLocations,
  practiceLocationObject,
}) => {
  const {
    control,
    setValue,
    trigger,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<PracticeLocationInfo>();

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [customAlertMessage, setCustomAlertMessage] = useState("");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { tokens, paperSx, closeIconSx, primaryBtnSx, cancelBtnSx, fieldWrapSx } = useModalStyles();

  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [stateList, setStateList] = useState<CodeTable[]>([]);

  const stateListOptions = useMemo(() => {
    return stateList.map((item) => ({
      label: item.displayValue,
      value: item.keyName,
    }));
  }, [stateList]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sorted = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "StateList"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setStateList(sorted);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (open) reset(practiceLocationObject);
    if (!open) reset();
  }, [open]);

  const fetchStreetSuggestions = async (query: string) => {
    if (query.length < 2) return [];
    try {
      return await dispatch(
        autoAddress({ addressInput: query, interceptorOption: { skipErrorPopup: true } })
      ).unwrap();
    } catch {
      return [];
    }
  };

  const transformAddressResponse = (response: any): any[] =>
    (response?.data || []).map((item: any) => ({
      value: item.description,
      data: item.placeId,
      label: item.description,
    }));

  const handleAddressSelect = async (value: any) => {
    if (!value?.data) return;
    try {
      const response = await dispatch(
        fetchAddressDetails({ placeID: value.data, interceptorOption: { skipErrorPopup: true } })
      ).unwrap();
      if (response?.data) {
        const { streetAddress, unitNumber, city, state, zipCode } = response.data;
        setValue("streetAddress", streetAddress);
        setValue("unitNumber", unitNumber || "");
        setValue("city", city);
        setValue("state", state);
        setValue("zipCode", zipCode);
        trigger(["city", "zipCode", "state"]);
      }
    } catch {
      console.error("Error fetching address details");
    }
  };

  const onSubmit = (data: PracticeLocationInfo) => {
    clearErrors();
    data.locationType = data.isBillingAddress === "yes" ? "BILLING" : "OTHER";

    if (data.isBillingAddress === "yes") {
      const alreadyHasBilling = practiceLocations?.some(
        (loc) => loc.isBillingAddress === "yes"
      );
      const isLocationTypeBilling = practiceLocations?.some(
        (loc) => loc.locationType === "BILLING" && loc.inactiveDate === null
      );

      if (alreadyHasBilling && practiceLocationObject.isBillingAddress !== "yes") {
        setCustomAlertMessage(
          "A billing address already exists. Please update the existing billing address instead."
        );
        setShowCustomAlert(true);
        return;
      } else if (isLocationTypeBilling && data.locationType === "BILLING") {
        setCustomAlertMessage(
          "A billing address already exists. Please inactivate the existing billing address and add a new address."
        );
        setShowCustomAlert(true);
        return;
      }
    }
    addPractice(data);
    onClose();
  };

  const isEditing = !!practiceLocationObject?.streetAddress;

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.88rem",
        fontWeight: 600,
        color: tokens.textPrimary,
        mb: 0.8,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const radioSx = {
    color: tokens.textSecondary,
    "&.Mui-checked": { color: tokens.accent },
    padding: "4px 8px",
  };

  const radioLabelSx = {
    fontSize: "0.85rem",
    color: tokens.textPrimary,
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.2,
            borderBottom: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary }}
          >
            {isEditing ? "Edit Practice Location" : "Add Practice Location"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={closeIconSx}>
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <DialogContent
          sx={{ px: 3, py: 3, overflowY: "auto", flex: 1, minHeight: 0 }}
        >
          {showCustomAlert && (
            <Box mb={2}>
              <CustomAlert
                open={showCustomAlert}
                onClose={() => setShowCustomAlert(false)}
                severity="error"
                message={customAlertMessage}
                alertTitle="Billing Address Alert"
              />
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldLabel label="Street Address" required />
              <Box sx={fieldWrapSx(!!errors?.streetAddress)}>
              <CustomAutocompleteField
                name="streetAddress"
                control={control}
                label=""
                isMultiple={false}
                isFreeSolo={true}
                fetchFunction={fetchStreetSuggestions}
                transformResponse={transformAddressResponse}
                onChangeSelect={handleAddressSelect}
                onChange={() => trigger(["city", "zipCode", "state"])}
                size="small"
                placeholderText="123 Main St"
                rules={{
                  required: "Street Address is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") return "Street Address is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel label="Unit Number" />
              <Box sx={fieldWrapSx()}>
              <CustomTextField
                controllerName="unitNumber"
                control={control}
                label=""
                size="small"
                placeholder="Suite 100"
              />
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FieldLabel label="City" required />
              <Box sx={fieldWrapSx(!!errors?.city)}>
              <CustomTextField
                controllerName="city"
                control={control}
                label=""
                size="small"
                placeholder="City"
                rules={{
                  required: "City is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") return "City is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FieldLabel label="State" required />
              <Box sx={fieldWrapSx(!!errors?.state)}>
              <CustomDropdownField
                name="state"
                control={control}
                label=""
                options={stateListOptions}
                size="small"
                placeholderText="State"
                sortOptions={false}
                rules={{
                  required: "State is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (!value) return "State is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FieldLabel label="Zip Code" required />
              <Box sx={fieldWrapSx(!!errors?.zipCode)}>
              <CustomTextField
                controllerName="zipCode"
                control={control}
                label=""
                size="small"
                placeholder="12345"
                rules={{
                  required: "Zip Code is required.",
                  validate: {
                    customValidation: (value: string) => {
                      if (value?.trim() === "") return "Zip Code is required.";
                    },
                  },
                }}
              />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel label="Is this also your billing address?" required />
              <FormControl fullWidth error={!!errors.isBillingAddress}>
                <CustomRadioButton
                  control={control}
                  controllerName="isBillingAddress"
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                  rules={{ required: "Billing Address is required." }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${
              isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"
            }`,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={
              <AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />
            }
            sx={primaryBtnSx}>
            {isEditing ? "Update Location" : "Add Location"}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default PracticeLocationPopup;